import { isToday } from 'date-fns';
import React, { useState, useMemo } from 'react';

// Define an interface for the expected payload structure (remains the same)
interface CallPayload {
  name: string;
  phone_number: string;
  vehicle_name: string;
  city: string;
  language: string;
  service_center: string;
  due_date: string;
  odometer: string;
  license_plate: string;
  collection_name: string;
  calling_number: string;
  bot_name: string;
  bot_intro?: string; // Optional field for bot introduction
  notification?: { // Optional notification preferences
    sms: boolean;
    whatsapp: boolean;
  };
}

interface CallFormProps {
  onSubmit: (payload: CallPayload, callType: 'with-crm' | 'without-crm') => void;
  isLoading: boolean;
  callType: 'with-crm' | 'without-crm';
  setCallType: React.Dispatch<React.SetStateAction<'with-crm' | 'without-crm'>>;
}

// --- Data for Dropdowns ---
// 2. Customer Phone/License Plate Data
const customerData: { [key: string]: string } = {
  "966501331741": "7184LBB",
  "966503007973": "7591ESB",
  // Add more customer phone/plate pairs here
};

const languages: string[] = ["Arabic", "English"]; // Example languages
// 3. City and Service Center Data
const cities: string[] = ["Riyadh"]; // Example cities
const serviceCenters: { [key: string]: string[] } = {
    "Riyadh": ["RYD Al Sahafa Branch"],
    "Jeddah": ["JED Main Branch", "JED North Branch"],
    "Dammam": ["DAM Corniche Branch", "DAM Central"],
    "Khobar": ["KHB Prince Turki St Branch"],
    "": [] // Handle case where no city is selected
}; // Example service centers grouped by city

// 4. Collection Name Data
const collectionNames: string[] = ["Testing", "Demo"];

// --- CallForm Component ---
const CallForm: React.FC<CallFormProps> = ({ onSubmit, isLoading, callType, setCallType }) => {
  // --- State for all form fields ---
  const [name, setName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState(''); // Target number (selected)
  const [vehicleName, setVehicleName] = useState('');
  const [language, setLanguage] = useState(languages[1]); // Selected Language
  const [city, setCity] = useState(cities[0]); // Selected city
  const [serviceCenter, setServiceCenter] = useState(serviceCenters[city][0]); // Selected service center
  const [dueDate, setDueDate] = useState(isToday(new Date()) ? new Date().toISOString().split('T')[0] : '');
  const [odometer, setOdometer] = useState('');
  const [licensePlate, setLicensePlate] = useState(''); // Set based on phone number
  const [collectionName, setCollectionName] = useState(collectionNames[1]); // Selected collection
  const [callingNumber, setCallingNumber] = useState(''); // Number *making* the call
  const [botName, setbotName] = useState('');
  const [botIntro, setBotIntro] = useState(''); // Optional bot introduction
  const [smsNotification, setSmsNotification] = useState(false); // New state for SMS notification
  const [whatsappNotification, setWhatsappNotification] = useState(false); // New state for WhatsApp notification

  const [error, setError] = useState('');

  // --- Derived State ---
  // Available service centers based on the selected city
  const availableServiceCenters = useMemo(() => {
      return serviceCenters[city] || [];
  }, [city]);

  type LicensePlate = "7184LBB" | "7591ESB";


  const vehicleData: { [key in LicensePlate]: string } = {
    "7184LBB": "Ford Explorer",
    "7591ESB": "Ford F150 Pickup",
  };
  // --- Event Handlers ---
  const handlePhoneNumberChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedPhoneNumber = event.target.value;
    setPhoneNumber(selectedPhoneNumber);
    setLicensePlate(customerData[selectedPhoneNumber]);
    setVehicleName(vehicleData[customerData[selectedPhoneNumber] as LicensePlate]);  
  };

  const handleVehicleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setVehicleName(event.target.value);
  };

  const handleLanguageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      const selectedLanguage = e.target.value;
      setLanguage(selectedLanguage);
      // Reset service center when city changes, as options will change
      //setServiceCenter('');
      setError('');
  };

  const handleServiceCenterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      setServiceCenter(e.target.value);
      setError('');
  }

  const handleCollectionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      setCollectionName(e.target.value);
      setError('');
  }

  // Basic phone number validation (for calling number)
  const validateCallingNumber = (number: string) => {
    const cleaned = number.replace(/\D/g, '');
    return cleaned.length >= 10; // Adjust as needed
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(''); // Clear previous errors

    // --- Validation ---
    if (!name.trim()) return setError('Customer Name is required.');
    if (!callingNumber.trim()) return setError('Calling Number is required.');
    if (!validateCallingNumber(callingNumber)) return setError('Invalid Calling Number format.');
    if (!phoneNumber) return setError('Please select a Customer Phone Number.'); // Dropdown validation
    // License plate is auto-set, so no direct validation needed here
    if (!vehicleName.trim()) return setError('Vehicle Name is required.');
    //if (!odometer.trim()) return setError('Odometer reading is required.');
    if (!botName.trim()) return setError('Bot Name is required.');
    if (!botIntro.trim()) return setError('Bot intro is required.');
    if (isNaN(Number(odometer))) return setError('Odometer must be a number.');
    if (!dueDate) return setError('Service Due Date is required.');
    if (!city) return setError('Please select a City.'); // Dropdown validation
    if (!language) return setError('Please select a Language.'); // Dropdown validation
    if (!serviceCenter) return setError('Please select a Service Center.'); // Dropdown validation
    if (!collectionName) return setError('Please select a Collection Name.'); // Dropdown validation

    const Odometer ="45000"; // Default value for Odometer
    // --- Construct Payload ---
    const payload: CallPayload = {
      name: name.trim(),
      phone_number: phoneNumber, // Selected from dropdown
      vehicle_name: vehicleName.trim(),
      city: city,               // Selected from dropdown                     
      language: language,               // Selected from dropdown
      service_center: serviceCenter,   // Selected from dropdown
      due_date: dueDate,
      odometer: Odometer,
      license_plate: licensePlate,   // Set based on phone number
      collection_name: collectionName, // Selected from dropdown
      calling_number: callingNumber.trim(),
      bot_name: botName,
      bot_intro: botIntro, // Optional field for bot introduction
      notification: { // Include notification preferences
        sms: smsNotification,
        whatsapp: whatsappNotification,
      },
    };

    // --- Submit ---
    onSubmit(payload, callType);
  };

  // Helper for standard input props (reuse where applicable)
  const getInputProps = (
        value: string,
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => void,
        placeholder: string,
        type: string = 'text',
        required: boolean = false, // Add required attribute if needed
        readOnly: boolean = false
    ) => ({
        type: type,
        placeholder: placeholder,
        className: `w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${readOnly ? 'bg-gray-100 cursor-not-allowed' : ''}`,
        value: value,
        onChange: onChange,
        disabled: isLoading,
        required: required, // HTML5 required attribute
        readOnly: readOnly
    });

   // Helper for select props
   const getSelectProps = (
        value: string,
        onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void,
        required: boolean = false
   ) => ({
        className: "w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white",
        value: value,
        onChange: onChange,
        disabled: isLoading,
        required: required
   });


  return (
    // 1. Wider container for two columns
    <div className="max-w-3xl w-full mx-auto p-6 bg-white rounded-lg shadow-lg transition-transform transform hover:scale-100">
      <h2 className="text-2xl font-semibold text-gray-800 mb-6 text-center">Make a Call</h2>
      {/* Use Grid for two columns, adjust gaps as needed */}
      <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">

        {/* Call Type Toggle */}
        <div className="md:col-span-2 flex items-center justify-center mb-4">
          <span className="text-gray-700 font-medium mr-3">Call Without CRM</span>
          <label htmlFor="toggle-crm" className="flex items-center cursor-pointer">
            <div className="relative">
              <input
                type="checkbox"
                id="toggle-crm"
                className="sr-only"
                checked={callType === 'with-crm'}
                onChange={() => setCallType(callType === 'with-crm' ? 'without-crm' : 'with-crm')}
                disabled={isLoading}
              />
              <div className="block bg-blue-700 w-14 h-8 rounded-full"></div>
              <div className="dot absolute left-1 top-1 bg-white w-6 h-6 rounded-full transition transform duration-300 ease-in-out"
                style={{ transform: callType === 'with-crm' ? 'translateX(24px)' : 'translateX(0)' }}></div>
            </div>
          </label>
          <span className="text-gray-700 font-medium ml-3">Call With Oorjit CRM</span>
        </div>


        {/* Column 1 */}
        <div className="space-y-1"> {/* Group Label and Input/Select */}
          <label htmlFor="name" className="block text-sm font-medium text-gray-700">Customer Name *</label>
          <input id="name" {...getInputProps(name, (e) => setName(e.target.value), 'e.g., Mr. Amar', 'text', true)} />
        </div>

        <div className="space-y-1">
           <label htmlFor="callingNumber" className="block text-sm font-medium text-gray-700">Your Calling Number *</label>
           <input id="callingNumber" {...getInputProps(callingNumber, (e) => setCallingNumber(e.target.value), 'e.g., +15550001111', 'tel', true)} />
        </div>

         {/* 2. Customer Phone Number Dropdown */}
         <div className="space-y-1">
          <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700">Customer Phone Number *</label>
          <select id="phoneNumber" {...getSelectProps(phoneNumber, handlePhoneNumberChange, true)}>
            <option value="" disabled>Select Phone Number...</option>
            {Object.keys(customerData).map((phone) => (
              <option key={phone} value={phone}>{phone}</option>
            ))}
          </select>
        </div>

        {/* 2. License Plate (Read Only) */}
        <div className="space-y-1">
          <label htmlFor="licensePlate" className="block text-sm font-medium text-gray-700">License Plate</label>
          <input id="licensePlate" {...getInputProps(licensePlate, () => {}, 'Auto-filled', 'text', false, true)} />
        </div>


        <div className="space-y-1">
        <label htmlFor="vehicleName" className="block text-sm font-medium text-gray-700">Vehicle Name</label>
        <input id="vehicleName" {...getInputProps(vehicleName, () => {}, 'Auto-filled', 'text', false, true)} />
      </div>


        {/* <div className="space-y-1">
           <label htmlFor="vehicleName" className="block text-sm font-medium text-gray-700">Vehicle Name *</label>
           <input id="vehicleName" {...getInputProps(vehicleName, (e) => setVehicleName(e.target.value), 'e.g., Ford Explorer', 'text', true)} />
        </div> */}

        <div className="space-y-1">
            <label htmlFor="dueDate" className="block text-sm font-medium text-gray-700">Service Due Date *</label>
            <input id="dueDate" {...getInputProps(dueDate, (e) => setDueDate(e.target.value), '', 'date', true)} />
        </div>

        {/* <div className="space-y-1">
           <label htmlFor="odometer" className="block text-sm font-medium text-gray-700">Odometer (km) *</label>
           <input id="odometer" {...getInputProps(odometer, (e) => setOdometer(e.target.value), 'e.g., 20300', 'number', true)} />
        </div> */}

        <div className="space-y-1">
           <label htmlFor="botName" className="block text-sm font-medium text-gray-700">Bot Name *</label>
           <input id="botName" {...getInputProps(botName, (e) => setbotName(e.target.value), 'e.g., Salma', 'text', true)} />
        </div>

        <div className="space-y-1">
           <label htmlFor="botIntro" className="block text-sm font-medium text-gray-700">Greetings *</label>
           <input id="botIntro" {...getInputProps(botIntro, (e) => setBotIntro(e.target.value), 'e.g., Hi! I am Salma', 'text', true)} />
        </div>

         {/* 3. Language Dropdown */}
          <div className="space-y-1">
          <label htmlFor="language" className="block text-sm font-medium text-gray-700">Language *</label>
          <select id="language" {...getSelectProps(language, handleLanguageChange, true)}>
            <option value="" disabled>Select Language...</option>
            {languages.map((language) => (
              <option key={language} value={language}>{language}</option>
            ))}
          </select>
        </div>

        {/* Notification Preferences */}
        <div className="md:col-span-2 space-y-2">
          <span className="block text-sm font-medium text-gray-700">Notification Preferences:</span>
          <div className="flex items-center space-x-4">
            <label htmlFor="sendSms" className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                id="sendSms"
                className="form-checkbox h-5 w-5 text-blue-600 rounded"
                checked={smsNotification}
                onChange={(e) => setSmsNotification(e.target.checked)}
                disabled={isLoading}
              />
              <span className="ml-2 text-gray-700">Send SMS</span>
            </label>
            <label htmlFor="sendWhatsapp" className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                id="sendWhatsapp"
                className="form-checkbox h-5 w-5 text-blue-600 rounded"
                checked={whatsappNotification}
                onChange={(e) => setWhatsappNotification(e.target.checked)}
                disabled={isLoading}
              />
              <span className="ml-2 text-gray-700">Send WhatsApp</span>
            </label>
          </div>
        </div>

        {/* Error Message Display - Spanning across columns */}
        {error && <p className="md:col-span-2 mt-1 text-sm text-red-600 text-center">{error}</p>}

        {/* Submit Button - Spanning across columns */}
        <div className="md:col-span-2 mt-4"> {/* Add margin top */}
            <button
              type="submit"
              className="w-full py-2.5 px-4 bg-primary text-white font-semibold rounded-lg hover:bg-blue-700 transition-all duration-300 disabled:opacity-50"
              disabled={isLoading}
            >
              {isLoading ? '🔄 Connecting...' : '  Call Now'}
            </button>
        </div>
      </form>
    </div>
  );
};

// --- TwilioCallApp Component (No changes needed here) ---
const TwilioCallApp: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [callStatus, setCallStatus] = useState<null | 'success' | 'error'>(null);
  const [callSid, setCallSid] = useState('');
  const [lastError, setLastError] = useState<string | null>(null);
  const [callType, setCallType] = useState<'with-crm' | 'without-crm'>('without-crm'); // New state for call type

  const makeCall = async (payload: CallPayload, selectedCallType: 'with-crm' | 'without-crm') => {
    setIsLoading(true);
    setCallStatus(null);
    setLastError(null);

    const baseUrl = 'https://pipecat-voice-bot-staging.ispgnet.com/';
    const endpoint = selectedCallType === 'with-crm' ? 'make-crm-call' : 'make-call';
    const apiUrl = `${baseUrl}${endpoint}`;

    console.log(`Sending payload to ${apiUrl}:`, payload);

    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      let data;
      try {
          data = await response.json();
      } catch (jsonError) {
          const textError = await response.text();
          console.error('Non-JSON response:', textError);
          data = { message: textError || 'Failed to parse response' };
      }


      if (data.status==="call_initiated") {
        setCallStatus('success');
        setCallSid(data.call_sid || 'N/A');
        console.log("Call initiated successfully", data);
      } else {
        console.error('API Error:', response.status, data);
        setCallStatus('error');
        setLastError(data?.message || data?.detail || `API returned status ${response.status}`);
      }
    } catch (error) {
      console.error('Fetch Error making call:', error);
      setCallStatus('error');
      setLastError(error instanceof Error ? error.message : 'An unknown network error occurred.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="flex items-center gap-3 bg-white p-4 border-b border-gray-200 shadow-sm">
        <h1 className="text-xl font-bold">📲 Invisible Assistant</h1>
      </div>
      {/* Adjusted padding and alignment for the main content area */}
      <div className="min-h-[calc(100vh-70px)] flex flex-col items-center pt-10 pb-6 bg-gray-100 px-4">
        <CallForm onSubmit={makeCall} isLoading={isLoading} callType={callType} setCallType={setCallType} />

        {/* Status Messages */}
        {callStatus === 'success' && (
          <div className="mt-6 p-4 bg-green-100 text-green-800 rounded-lg shadow-md text-center w-full max-w-3xl">
            ✅ Call initiated successfully! <br />
            <span className="text-sm">Call SID: {callSid}</span>
          </div>
        )}
        {callStatus === 'error' && (
          <div className="mt-6 p-4 bg-red-100 text-red-800 rounded-lg shadow-md text-center w-full max-w-3xl">
            Booking already exists for the selected license plate <br />
            {lastError && <span className="text-sm font-medium">Details: {lastError}</span>}
          </div>
        )}
      </div>
    </>
  );
};

export default TwilioCallApp;